import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase, getAdminApiUrl } from '../lib/supabase';

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  profile_type: string;
  business_name: string;
  abn_number: string;
  created_at: string;
  updated_at: string;
  country_code: string;
  phone: string;
  about_text: string;
  individual_image_url: string;
  business_image_url: string;
  business_expertise: string;
  default_profile: string;
  email: string;
  stripe_account_id: string;
  stripe_customer_id: string;
  expo_push_token: string;
  country_name: string;
  currency_code: string;
  currency_symbol: string;
  currency_name: string;
  is_admin: boolean;
  email_verified: boolean;
  email_verified_at: string;
  phone_verified: boolean;
  phone_verified_at: string;
  business_stripe_account_id: string;
  business_stripe_customer_id: string;
  business_rep_first_name: string;
  business_rep_last_name: string;
  city_name: string;
  state_name: string;
  city_latitude: number;
  city_longitude: number;
  state_latitude: number;
  state_longitude: number;
  phone_country_code: string;
  admin_role: string;
  is_confirmed?: boolean;
  last_sign_in?: string;
  user_metadata?: {
    force_logout?: number;
    force_logout_initiated_at?: string;
    force_logout_initiated_by?: string;
    [key: string]: any;
  };
}

interface GlobalAppState {
  id: string;
  key: string;
  value: string;
  description: string;
  created_at: string;
  updated_at: string;
}

interface AdminNote {
  id: string;
  user_id: string;
  admin_id: string;
  note_content: string;
  note_type: 'general' | 'support' | 'warning' | 'ban' | 'unban' | 'verification' | 'payment' | 'other';
  is_important: boolean;
  created_at: string;
  updated_at: string;
  admin_email?: string;
  admin_first_name?: string;
  admin_last_name?: string;
  admin_business_name?: string;
  admin_role?: string;
}

const UserProfileView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { adminRole } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [globalAppStates, setGlobalAppStates] = useState<GlobalAppState[]>([]);
  const [adminNotes, setAdminNotes] = useState<AdminNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteType, setNewNoteType] = useState<'general' | 'support' | 'warning' | 'ban' | 'unban' | 'verification' | 'payment' | 'other'>('general');
  const [newNoteImportant, setNewNoteImportant] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [banDuration, setBanDuration] = useState<number>(24); // hours
  const [banStatus, setBanStatus] = useState<{
    isBanned: boolean;
    banDuration?: string;
    bannedUntil?: string;
  } | null>(null);

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
      fetchGlobalAppStates();
      fetchAdminNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const updateBanStatus = (userData: any) => {
    // Check if user is banned by looking at user_metadata.banned_until
    const bannedUntil = userData.user_metadata?.banned_until;
    const bannedAt = userData.user_metadata?.banned_at;
    const unbannedAt = userData.user_metadata?.unbanned_at;
    
    // If user has been unbanned, they are not currently banned
    if (unbannedAt) {
      setBanStatus({
        isBanned: false,
        banDuration: undefined,
        bannedUntil: undefined
      });
      return;
    }
    
    if (bannedUntil) {
      const now = new Date();
      const banEndTime = new Date(bannedUntil);
      const isCurrentlyBanned = now < banEndTime;
      
      // Calculate ban duration from banned_at to banned_until
      let banDuration: string | undefined;
      if (bannedAt) {
        const banStartTime = new Date(bannedAt);
        const durationMs = banEndTime.getTime() - banStartTime.getTime();
        const durationHours = Math.round(durationMs / (1000 * 60 * 60));
        
        if (durationHours < 24) {
          banDuration = `${durationHours}h`;
        } else {
          const durationDays = Math.round(durationHours / 24);
          banDuration = `${durationDays}d`;
        }
      }
      
      setBanStatus({
        isBanned: isCurrentlyBanned,
        banDuration,
        bannedUntil: bannedUntil
      });
    } else {
      setBanStatus({
        isBanned: false,
        banDuration: undefined,
        bannedUntil: undefined
      });
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getUserById',
          userId: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
        updateBanStatus(data.user);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch user profile');
      }
    } catch (err) {
      setError('Error fetching user profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalAppStates = async () => {
    try {
      const { data: globalAppStates, error } = await supabase
        .from('global_app_state')
        .select('*')
        .order('key');

      if (error) {
        return;
      }

      setGlobalAppStates(globalAppStates);
      // Set maintenance mode state
      const maintenanceState = globalAppStates.find((state: GlobalAppState) => state.key === 'maintenance_mode');
      setMaintenanceMode(maintenanceState?.value === 'true');
    } catch (err) {
      // Error fetching global app states
    }
  };

  const fetchAdminNotes = async () => {
    try {
      setNotesLoading(true);
      const { data: notes, error } = await supabase
        .from('admin_notes_with_details')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        return;
      }

      setAdminNotes(notes || []);
    } catch (err) {
      // Error fetching admin notes - silently handle to prevent UI disruption
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      alert('Please enter a note');
      return;
    }

    try {
      setAddingNote(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        alert('No active session');
        return;
      }

      const { error } = await supabase
        .from('admin_notes')
        .insert({
          user_id: userId,
          admin_id: session.user.id,
          note_content: newNote.trim(),
          note_type: newNoteType,
          is_important: newNoteImportant
        })
        .select()
        .single();

      if (error) {
        alert('Failed to add note');
        return;
      }

      // Clear form
      setNewNote('');
      setNewNoteType('general');
      setNewNoteImportant(false);
      
      // Refresh notes
      await fetchAdminNotes();
      
      alert('Note added successfully');
    } catch (err) {
      alert('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('admin_notes')
        .delete()
        .eq('id', noteId);

      if (error) {
        alert('Failed to delete note');
        return;
      }

      // Refresh notes
      await fetchAdminNotes();
      alert('Note deleted successfully');
    } catch (err) {
      alert('Failed to delete note');
    }
  };

  const handleForceLogoutAllUsers = async () => {
    try {
      setToggleLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      // Confirm action with user
      if (!window.confirm('🚨 WARNING: This will immediately log out ALL users from ALL devices!\n\nAre you sure you want to proceed?')) {
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'forceLogoutAllUsers'
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ All users have been force logged out!\n\nTotal users: ${result.total_users}\nSuccess: ${result.success_count}\nErrors: ${result.error_count}\n\nTimestamp: ${new Date(result.force_logout_timestamp).toLocaleString()}`);
      } else {
        const errorData = await response.json();
        alert(`❌ Force logout failed: ${errorData.error}`);
      }
    } catch (err) {
      setError('Error during force logout');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleAdminAction = async (action: string, data?: any) => {
    try {
      setActionLoading(action);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action,
          userId,
          data
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Special handling for different actions
        if (action === 'forceLogout') {
          const timestamp = new Date(result.force_logout_timestamp).toLocaleString();
          alert(`✅ Force logout successful!\n\nUser will be logged out immediately.\n\nTimestamp: ${timestamp}\n\nNote: The user will see a notification and be automatically signed out.`);
        } else if (action === 'banUser') {
          // Immediately set ban status for instant UI feedback
          const banDurationHours = result.ban_duration_hours || 24;
          const bannedUntil = result.banned_until || new Date(Date.now() + banDurationHours * 60 * 60 * 1000).toISOString();
          
          setBanStatus({
            isBanned: true,
            banDuration: `${banDurationHours}h`,
            bannedUntil: bannedUntil
          });
          
          alert(`✅ User banned successfully!\n\nBan Duration: ${result.ban_duration_hours || 'Unknown'} hours\nBanned Until: ${result.banned_until ? new Date(result.banned_until).toLocaleString() : 'Unknown'}\n\nThe user cannot access the platform until the ban expires.`);
        } else if (action === 'unbanUser') {
          // Immediately clear ban status for instant UI feedback
          setBanStatus({
            isBanned: false,
            banDuration: undefined,
            bannedUntil: undefined
          });
          alert(`✅ User unbanned successfully!\n\nUnbanned at: ${result.unbanned_at ? new Date(result.unbanned_at).toLocaleString() : 'Unknown'}\n\nUser access has been restored immediately.\n\nThe ban status banner will disappear and the user can now access the platform.`);
        } else {
          alert(`Action successful: ${result.message || 'Action completed'}`);
        }
        
        // Refresh profile data to update ban status
        await fetchUserProfile();
      } else {
        const errorData = await response.json();
        alert(`Action failed: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateLink = async (type: string) => {
    try {
      setActionLoading(`generateLink_${type}`);
      
      // Check if user has a valid email
      if (!profile?.email) {
        alert('No email address available for this user');
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const requestBody = {
        action: 'generateLink',
        data: {
          type,
          email: profile?.email
        }
      };

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.message) {
          // New format: resetPasswordForEmail returns message and email
          alert(`✅ ${result.message}`);
        } else if (result.link) {
          // Old format: generateLink returns link
          alert(`Link generated successfully: ${result.link}`);
        } else if (result.properties?.action_link) {
          // Alternative format
          alert(`Link generated successfully: ${result.properties.action_link}`);
        } else {
          alert(`Action completed but response format is unexpected.`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to generate link: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendPhoneOTP = async () => {
    if (!profile?.phone) {
      alert('No phone number available for this user');
      return;
    }

    try {
      setActionLoading('sendPhoneOTP');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      const response = await fetch(getAdminApiUrl(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'sendPhoneOTP',
          data: {
            phone: profile.phone
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Phone OTP sent successfully: ${result.message}`);
      } else {
        const errorData = await response.json();
        alert(`Failed to send OTP: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMaintenanceModeToggle = async () => {
    try {
      setToggleLoading(true);
      const newState = !maintenanceMode;
      
      // Directly update the global_app_state table
      const { error } = await supabase
        .from('global_app_state')
        .update({ 
          value: newState.toString(),
          updated_at: new Date().toISOString()
        })
        .eq('key', 'maintenance_mode');

      if (error) {
        setError('Failed to update maintenance mode');
        return;
      }

      // If enabling maintenance mode, force logout all users
      if (newState) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            const response = await fetch(getAdminApiUrl(), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                action: 'forceLogoutAllUsers'
              })
            });

            if (response.ok) {
              const result = await response.json();
              // Show success message
              alert(`✅ Maintenance mode enabled!\n\nAll users have been force logged out.\n\nTotal users: ${result.total_users}\nSuccess: ${result.success_count}\nErrors: ${result.error_count}`);
            } else {
              const errorData = await response.json();
              alert(`⚠️ Maintenance mode enabled, but force logout failed: ${errorData.error}`);
            }
          }
        } catch (forceLogoutError) {
          alert(`⚠️ Maintenance mode enabled, but force logout failed. Users may remain logged in.`);
        }
      }

      setMaintenanceMode(newState);
      // Update local global app states
      setGlobalAppStates(prev => 
        prev.map(state => 
          state.key === 'maintenance_mode' 
            ? { ...state, value: newState.toString(), updated_at: new Date().toISOString() }
            : state
        )
      );
    } catch (err) {
      setError('Error updating maintenance mode');
    } finally {
      setToggleLoading(false);
    }
  };

  if (adminRole !== 'super_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-gray-600 mb-4">
            This page is only accessible to Super Administrators.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Profile</h3>
          <p className="text-gray-600 mb-4">{error || 'User profile not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">User Profile View</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Ban Status Banner */}
      {banStatus?.isBanned && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <div className="flex items-center space-x-2">
                <h3 className="text-sm font-medium text-red-800">
                  🚫 User is Currently Banned
                </h3>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  BANNED
                </span>
              </div>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <strong>Ban Duration:</strong> {banStatus.banDuration}
                </p>
                {banStatus.bannedUntil && (
                  <p>
                    <strong>Ban Expires:</strong> {new Date(banStatus.bannedUntil).toLocaleString()}
                  </p>
                )}
                <p className="mt-1">
                  This user cannot access the platform until the ban expires or is manually removed.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Info Card */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">User Information</h2>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <p className="text-sm text-gray-900">{profile.email || 'No email'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
                    <p className="text-sm text-gray-900 font-mono">{profile.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Profile Type</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      profile.profile_type === 'business' 
                        ? 'bg-purple-100 text-purple-800'
                        : profile.profile_type === 'individual'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {profile.profile_type === 'business' 
                        ? 'Business' 
                        : profile.profile_type === 'individual'
                        ? 'Individual'
                        : 'Unknown'
                      }
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Admin Role</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      profile.is_admin 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {profile.is_admin ? (profile.admin_role || 'Admin') : 'User'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Joined</label>
                    <p className="text-sm text-gray-900">
                      {new Date(profile.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Sign In</label>
                    <p className="text-sm text-gray-900">
                      {profile.last_sign_in 
                        ? new Date(profile.last_sign_in).toLocaleDateString()
                        : 'Never'
                      }
                    </p>
                  </div>
                </div>

                {/* Display Name */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                  {profile.profile_type === 'business' && profile.business_name ? (
                    <p className="text-sm text-gray-900">{profile.business_name}</p>
                  ) : profile.first_name || profile.last_name ? (
                    <p className="text-sm text-gray-900">
                      {[profile.first_name, profile.last_name].filter(Boolean).join(' ')}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No name provided</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Global App State Card */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Global App State</h2>
                <p className="text-sm text-gray-500 mt-1">Control application-wide settings</p>
              </div>
              <div className="px-6 py-4 space-y-4">
                {/* Maintenance Mode Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                    <p className="text-xs text-gray-500 mt-1">
                      When enabled, shows maintenance splash screen and blocks app access
                    </p>
                  </div>
                  <button
                    onClick={handleMaintenanceModeToggle}
                    disabled={toggleLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      maintenanceMode ? 'bg-red-600' : 'bg-gray-200'
                    } ${toggleLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-md">
                  <p className="text-xs text-gray-600">
                    <strong>Current Status:</strong> {maintenanceMode ? 'Enabled' : 'Disabled'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {maintenanceMode 
                      ? 'App is in maintenance mode - users will see maintenance screen'
                      : 'App is running normally'
                    }
                  </p>
                </div>

                {/* Manual Force Logout All Users Button */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Emergency Actions</h3>
                  <button
                    onClick={handleForceLogoutAllUsers}
                    disabled={toggleLoading}
                    className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {toggleLoading ? 'Processing...' : '🚨 Force Logout All Users'}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">
                    Immediately logs out all users from all devices. Use this for emergency situations or maintenance.
                  </p>
                </div>

                {/* Other Global App States */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Other Global Settings</h3>
                  <div className="space-y-2">
                    {globalAppStates
                      .filter(state => state.key !== 'maintenance_mode')
                      .map((state) => (
                        <div key={state.id} className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">{state.key.replace(/_/g, ' ')}:</span>
                          <span className="text-gray-900 font-medium">{state.value}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Actions Section */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">User Actions</h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Email Functions */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Email Functions</h3>
                <div className="space-y-3">
                  <div>
                    <button
                      onClick={() => handleGenerateLink('magiclink')}
                      disabled={actionLoading === 'generateLink_magiclink'}
                      className="w-full px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'generateLink_magiclink' ? 'Generating...' : 'Send Magic Link'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Sends a secure login link to the user's email address for passwordless authentication</p>
                  </div>
                  <div>
                    <button
                      onClick={() => handleGenerateLink('recovery')}
                      disabled={actionLoading === 'generateLink_recovery'}
                      className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'generateLink_recovery' ? 'Generating...' : 'Send Password Reset'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Sends a password reset link to the user's email address to help them regain account access</p>
                  </div>
                </div>
              </div>

              {/* Phone Functions */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Phone Functions</h3>
                <div>
                  <button
                    onClick={handleSendPhoneOTP}
                    disabled={actionLoading === 'sendPhoneOTP' || !profile?.phone}
                    className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {actionLoading === 'sendPhoneOTP' ? 'Sending...' : 'Send Phone OTP'}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">Sends a one-time password (OTP) to the user's registered phone number for verification purposes</p>
                </div>
              </div>

              {/* Session Management */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Session Management</h3>
                <div className="space-y-3">
                  <div>
                    <button
                      onClick={() => handleAdminAction('forceLogout')}
                      disabled={actionLoading === 'forceLogout'}
                      className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'forceLogout' ? 'Processing...' : 'Force Logout'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Immediately terminates the user's current session and logs them out from all devices</p>
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={banDuration}
                        onChange={(e) => setBanDuration(Number(e.target.value))}
                        min="1"
                        max="8760"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Hours"
                      />
                      <button
                        onClick={() => handleAdminAction('banUser', { banDuration: banDuration * 3600 })}
                        disabled={actionLoading === 'banUser'}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        {actionLoading === 'banUser' ? 'Banning...' : 'Ban User'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Temporarily restricts user access to the platform for the specified duration (1-8760 hours)</p>
                  </div>
                  
                  <div>
                    <button
                      onClick={() => handleAdminAction('unbanUser')}
                      disabled={actionLoading === 'unbanUser'}
                      className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {actionLoading === 'unbanUser' ? 'Unbanning...' : 'Unban User'}
                    </button>
                    <p className="mt-1 text-xs text-gray-500">Immediately removes any active ban and restores user access to the platform</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Notes Section */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Admin Notes</h2>
              <p className="text-sm text-gray-500 mt-1">Add and view interaction notes for this user</p>
            </div>
            
            {/* Add New Note Form */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add New Note</label>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter your note here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note Type</label>
                    <select
                      value={newNoteType}
                      onChange={(e) => setNewNoteType(e.target.value as any)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="general">General</option>
                      <option value="support">Support</option>
                      <option value="warning">Warning</option>
                      <option value="ban">Ban</option>
                      <option value="unban">Unban</option>
                      <option value="verification">Verification</option>
                      <option value="payment">Payment</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="important"
                      checked={newNoteImportant}
                      onChange={(e) => setNewNoteImportant(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="important" className="ml-2 block text-sm text-gray-700">
                      Mark as Important
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </button>
                </div>
              </div>
            </div>

            {/* Notes List */}
            <div className="px-6 py-4">
              {notesLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading notes...</p>
                </div>
              ) : adminNotes.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500">No notes yet. Add the first note above.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {adminNotes.map((note) => (
                    <div key={note.id} className={`border rounded-lg p-4 ${note.is_important ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            note.note_type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            note.note_type === 'ban' ? 'bg-red-100 text-red-800' :
                            note.note_type === 'unban' ? 'bg-green-100 text-green-800' :
                            note.note_type === 'support' ? 'bg-blue-100 text-blue-800' :
                            note.note_type === 'verification' ? 'bg-purple-100 text-purple-800' :
                            note.note_type === 'payment' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {note.note_type.charAt(0).toUpperCase() + note.note_type.slice(1)}
                          </span>
                          {note.is_important && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              ⚠️ Important
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="Delete note"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      
                      <p className="text-gray-900 mb-3 whitespace-pre-wrap">{note.note_content}</p>
                      
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <div>
                          <span className="font-medium">
                            {note.admin_business_name || 
                             (note.admin_first_name && note.admin_last_name ? 
                              `${note.admin_first_name} ${note.admin_last_name}` : 
                              note.admin_email)}
                          </span>
                          {note.admin_role && (
                            <span className="ml-2 px-1 py-0.5 bg-gray-100 rounded text-gray-600">
                              {note.admin_role}
                            </span>
                          )}
                        </div>
                        <div>
                          {new Date(note.created_at).toLocaleString()}
                          {note.updated_at !== note.created_at && (
                            <span className="ml-2 text-gray-400">(edited)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileView;
